import { useState } from 'react';

import { Box, Button, Link, Text, useMedia, ValidatedTextInput } from '@opendoor/bricks-next';
import { Loader } from '@opendoor/bricks/core';
import isEmail from 'validator/lib/isEmail';

import { PageContent, PageHeader, PageLayout } from 'src/components/layouts/PageLayout';
import { ReceptionLoader } from 'src/components/ReceptionLoader';
import { WEB_API_URL } from 'src/helpers/globals';
import { isOpendoorAppEvoWebView } from 'src/helpers/webview';
import {
  useLazyRequestListedOfferQuery,
  useResendActiveOfferMutation,
  useVerifyActiveOfferQuery,
} from 'src/store/api';
import { useAppSelector } from 'src/store/hooks';
import {
  address as getAddress,
  lastListing as getLastListing,
  leadToken as getLeadToken,
} from 'src/store/sellerFlow/selectors';
import { Listing, SellerInputAddress } from 'src/store/sellerInput';

import AddressLine from '../../components/common/AddressLine';
import ErrorNotice from '../../components/common/ErrorNotice';
import { DenialCard, DenialHeader, DenialTextAfterHeader } from './FlowDeniedNovo';

const ListingNotFound: React.FC<{ address: SellerInputAddress }> = ({ address }) => (
  <>
    <Box>
      <DenialHeader>We couldn't find a listing for that home</DenialHeader>
      <DenialTextAfterHeader>
        Recently listed homes can take a day or two to show up in our system.
      </DenialTextAfterHeader>
    </Box>
    <AddressLine address={address} />
    <ErrorNotice />
  </>
);

const ListingDetails = ({ listing }: { listing: Listing }) => {
  return (
    <Box gap="$8x" display="flex">
      {listing.listing_id ? (
        <ListingDetail label="MLS Listing ID">{listing.listing_id}</ListingDetail>
      ) : null}
      {listing.realtor_name ? (
        <ListingDetail label="Listed by">{listing.realtor_name.trim()}</ListingDetail>
      ) : null}
      {listing.realtor_email ? (
        <ListingDetail label="Email">{listing.realtor_email}</ListingDetail>
      ) : null}
    </Box>
  );
};

type ListingDetailProps = React.PropsWithChildren<{
  label: string;
}>;
const ListingDetail: React.FC<ListingDetailProps> = ({ label, children }) => (
  <Box
    display="flex"
    flexDirection="column"
    $largerThanMD={{
      flexDirection: 'row',
      alignItems: 'center',
    }}
    alignItems="flex-start"
    gap="$4x"
  >
    <Text
      typography="$labelMedium"
      $largerThanMD={{ fontSize: '$labelLarge' }}
      tag="label"
      color="$contentPrimary"
    >
      {label}
    </Text>
    <Text
      typography="$bodyMedium"
      $largerThanMD={{ fontSize: '$bodyLarge' }}
      tag="span"
      color="$contentTertiary"
    >
      {children}
    </Text>
  </Box>
);

const ContactForm = ({
  listing,
  onChangeName,
  onChangeEmail,
}: {
  listing: Listing;
  onChangeName: (name: string) => void;
  onChangeEmail: (name: string) => void;
}) => {
  const media = useMedia();
  const labelSize = media.largerThanMD ? 'medium' : 'small';

  return (
    <form id="contact_form">
      {!listing.realtor_name && (
        <Box mb={16} pb={4}>
          <ValidatedTextInput
            analyticsName="reception-fe-flow-verifying-listing-full-name"
            textContentType="name"
            name="name"
            size={labelSize}
            title="Enter agent's full name"
            id="verifying-listing-name"
            placeholder="Full Name"
            aria-label="Listing agent's full name"
            onChangeText={onChangeName}
            required
            format="none"
          />
        </Box>
      )}
      {!isValidEmail(listing.realtor_email ?? '') && (
        <Box mb={7} pb={4}>
          <ValidatedTextInput
            allowSuggestions
            name="agent_email"
            analyticsName="reception-fe-flow-verifying-listing-full-email"
            size={labelSize}
            title="Enter agent's email"
            supportText="We’ll send details on how to request an offer."
            textContentType="emailAddress"
            id="verifying-listing-email"
            placeholder="name@email.com"
            aria-label="Listing agent's email"
            onChangeText={onChangeEmail}
            required
            format="emailAddress"
          />
        </Box>
      )}
    </form>
  );
};

const EmailSentToAgentConfirmation: React.FC = () => {
  const listing = useAppSelector(getLastListing);
  return (
    <>
      <Box>
        <DenialHeader>An email is on the way to your agent</DenialHeader>
        <DenialTextAfterHeader>
          They’ll receive details on how to get an offer from Opendoor shortly.
        </DenialTextAfterHeader>
      </Box>
      <ListingDetails listing={listing} />
      <ErrorNotice />
    </>
  );
};

const ActiveListingResendOffer: React.FC<{ offerId: string }> = ({ offerId }) => {
  const address = useAppSelector(getAddress);
  const listing = useAppSelector(getLastListing);
  const [resendActiveOffer, { data, isLoading, isError }] = useResendActiveOfferMutation();

  if (data) {
    return <EmailSentToAgentConfirmation />;
  }

  return (
    <>
      <Box>
        <DenialHeader>This home is listed. We’ve sent an offer.</DenialHeader>
        <DenialTextAfterHeader>
          We sent an offer for this home to the listing agent. Want us to send it again?
        </DenialTextAfterHeader>
      </Box>
      <ListingDetails listing={listing} />

      <Box>
        {isError && (
          <Text typography="$bodyMedium" tag="p" color="red">
            We reached an error sending the offer. Please try again.
          </Text>
        )}
        <Box mt={4}>
          <Button
            label="Resend offer to agent"
            id="od-form-submit"
            onPress={() => {
              resendActiveOffer({ offerId });
            }}
            size="small"
            variant="primary"
            aria-label={undefined}
            analyticsName="reception-fe-verify-listing-submit-email"
            loading={isLoading}
          />
        </Box>
      </Box>
      <AddressLine address={address} />
      <ErrorNotice />
    </>
  );
};

// TODO - export this from bricks-next instead of copying it here
// This is used so we can validate that the listing.realtor_email is valid when it is present,
// additionally we currently use this so we can enable the button when we have a user input the email
// we could use onValidationChange from ValidatedTextInput, but since we need to check our initial email
// anyway it made sense to do it the same way for both listing.realtor_email and user input.
const BLACKLISTED_TLDS = ['como', 'comp', 'con', 'met', 'ner', 'vom'];
const isValidEmail = (value: string): boolean => {
  const tld = value.split('.').pop();
  const blackListed = tld ? BLACKLISTED_TLDS.includes(tld) : false;
  return isEmail(value) && !blackListed;
};

const ActiveListing: React.FC = () => {
  const sellerInputUUID = useAppSelector(getLeadToken);
  const [requestListedOffer, { data, isLoading, isError }] = useLazyRequestListedOfferQuery();

  const address = useAppSelector(getAddress);
  const listing = useAppSelector(getLastListing);
  const [realtorEmail, setRealtorEmail] = useState(listing?.realtor_email || '');
  const [realtorName, setRealtorName] = useState(listing?.realtor_name || '');

  if (data) {
    return <EmailSentToAgentConfirmation />;
  }

  const btnDisabled = !isValidEmail(realtorEmail) || !realtorName;

  let denialTextAfterHeader: React.ReactNode = null;
  if (listing.realtor_email) {
    denialTextAfterHeader = (
      <DenialTextAfterHeader>
        If this home is eligible, we’ll send an offer to the email below.
      </DenialTextAfterHeader>
    );
  }

  return (
    <>
      <Box>
        <DenialHeader>This home is currently listed on the market</DenialHeader>
        {denialTextAfterHeader}
      </Box>
      <ListingDetails listing={listing} />

      <Box>
        <ContactForm
          listing={listing}
          onChangeEmail={(email: string) => {
            setRealtorEmail(email);
          }}
          onChangeName={(name: string) => {
            setRealtorName(name);
          }}
        />
        {isError && (
          <Text typography="$bodyMedium" tag="p" color="red">
            We reached an error sending the offer. Please try again.
          </Text>
        )}
        <Box mt={4}>
          <Button
            label="Send offer to agent"
            id="od-form-submit"
            onPress={() => {
              requestListedOffer({ realtorName, realtorEmail, sellerInputUUID });
            }}
            size="small"
            variant="primary"
            disabled={btnDisabled}
            aria-label={undefined}
            analyticsName="reception-fe-verify-listing-submit-email"
            loading={isLoading}
          />
        </Box>
      </Box>
      <AddressLine address={address} />
      <ErrorNotice />
    </>
  );
};

const VerifyingListing: React.FC = () => {
  const address = useAppSelector(getAddress);
  const listing = useAppSelector(getLastListing);

  const { data: activeOfferData, isLoading: isActiveOfferLoading } = useVerifyActiveOfferQuery(
    {
      email: listing.realtor_email ?? '',
      addressUUID: address.uuid,
    },
    { skip: !listing.realtor_email },
  );

  if (!listing.is_listed_and_eligible) {
    return <ListingNotFound address={address} />;
  }

  if (isActiveOfferLoading) {
    return (
      <Box height={300} display="flex" justifyContent="center" alignItems="center">
        <Loader />
      </Box>
    );
  }

  if (activeOfferData && activeOfferData.experiencePartner) {
    return <ActiveListingResendOffer offerId={activeOfferData.id} />;
  }

  return <ActiveListing />;
};

const ActiveListingDenialPage = () => (
  <ReceptionLoader>
    <PageLayout>
      <PageHeader>
        <Link
          href={WEB_API_URL}
          aria-label="Exit"
          analyticsName="reception-fe-exit"
          typography={'$labelMedium'}
        >
          Exit
        </Link>
      </PageHeader>
      <PageContent>
        <DenialCard isOpendoorAppEvoWebView={isOpendoorAppEvoWebView}>
          <VerifyingListing />
        </DenialCard>
      </PageContent>
    </PageLayout>
  </ReceptionLoader>
);

export default ActiveListingDenialPage;
